import { Controller } from '@hotwired/stimulus'
import { getInstanceManager } from '@pagefind/component-ui'
import { useClickOutside } from 'stimulus-use'
import * as focusTrap from 'focus-trap'

const EXCERPT_LEAD_WORDS = 4

export default class extends Controller {
  static outlets = ['modal']
  static targets = [
    'input',
    'macKbd',
    'nonMacKbd',
    'root',
    'modal',
    'resultsHeading',
    'noResults',
    'searchTerm',
    'results',
    'entryTemplate'
  ]
  static values = { id: String }

  initialize() {
    this.boundDeactivateFocusTrap = this.#deactivateFocusTrap.bind(this)
  }

  connect() {
    useClickOutside(this, { element: this.modalTarget })
    this.#showCorrectPlatformKbd()
    this.#initPagefind()
    this.focusTrap = focusTrap.createFocusTrap(this.modalTarget)
    this.modalTarget.addEventListener('close', this.boundDeactivateFocusTrap)
  }

  disconnect() {
    if (this.modalTarget && this.boundDeactivateFocusTrap) {
      this.modalTarget.removeEventListener(
        'close',
        this.boundDeactivateFocusTrap
      )
    }

    if (this.focusTrap) {
      this.focusTrap.deactivate()
      this.focusTrap = null
    }
  }
  clickOutside() {
    this.hide()
  }

  hide(_event) {
    if (!this.modalTarget.open) return

    this.modalOutlet.close()
    this.boundDeactivateFocusTrap()
  }

  show(event) {
    if (this.modalTarget.open) return

    event.preventDefault()
    this.modalOutlet.open()
    this.inputTarget.focus()
    this.focusTrap.activate()
  }

  query() {
    clearTimeout(this.debounceTimeout)

    this.debounceTimeout = setTimeout(() => {
      this.pagefindInstance.triggerSearch(this.inputTarget.value)
    }, 300)
  }

  loadBundle() {
    this.pagefindInstance.triggerLoad()
  }

  focusResults() {
    this.pagefindInstance.focusNextResults(this.inputTarget)
  }

  #initPagefind() {
    this.pagefindInstance = getInstanceManager().getInstance(this.idValue)

    // Pagefind's focus helpers look for an inputEl property when moving
    // focus back to a registered input
    this.inputTarget.inputEl = this.inputTarget
    this.pagefindInstance.registerInput(this.inputTarget, {
      keyboardNavigation: true
    })

    this.resultsTarget.resultTemplate = this.#renderResult.bind(this)

    this.pagefindInstance.on(
      'results',
      results => {
        this.resultsHeadingTarget.hidden = !this.inputTarget.value
        const noResults = this.inputTarget.value && !results.results.length
        if (noResults) {
          this.searchTermTarget.textContent = this.inputTarget.value
        }
        this.noResultsTarget.hidden = !noResults
      },
      this
    )
  }

  #renderResult(result) {
    const { title, image, image_alt } = result.meta
    const { excerpt, url } = result
    const templateHTML = this.entryTemplateTarget.content.cloneNode(true)
    const imageContainer = templateHTML.querySelector('.search-result-image')
    const imageFallback = templateHTML.querySelector(
      '.search-result-image-fallback'
    )

    if (imageContainer) {
      imageContainer.innerHTML = ''

      if (image) {
        const imgEl = document.createElement('img')
        imgEl.src = image
        imgEl.alt = image_alt || ''
        imgEl.className = 'object-cover size-full'
        imageContainer.appendChild(imgEl)
        imageContainer.classList.remove('hidden')
        imageFallback?.classList.add('hidden')
      } else {
        imageContainer.classList.add('hidden')
        imageFallback?.classList.remove('hidden')
      }
    }

    templateHTML.querySelector('a').href = url
    templateHTML.querySelector('.article-title').innerHTML = title
    templateHTML.querySelector('.article-subtitle').innerHTML =
      this.#excerptWithVisibleMatch(excerpt)
    return templateHTML.firstElementChild
  }

  // Pagefind centers its excerpt on the match, which pushes the <mark> past
  // the end of our single-line subtitle, so drop all but a few lead words
  #excerptWithVisibleMatch(excerpt) {
    const matchIndex = excerpt.indexOf('<mark')
    if (matchIndex === -1) return excerpt

    const lead = excerpt.slice(0, matchIndex).trimEnd().split(' ')
    if (lead.length <= EXCERPT_LEAD_WORDS) return excerpt

    const visibleLead = lead.slice(-EXCERPT_LEAD_WORDS).join(' ')

    return `&hellip;${visibleLead} ${excerpt.slice(matchIndex)}`
  }

  #deactivateFocusTrap() {
    this.focusTrap.deactivate()
  }

  #showCorrectPlatformKbd() {
    if (navigator.userAgentData?.platform === 'macOS') {
      this.macKbdTargets.forEach(target => (target.hidden = false))
    } else {
      this.nonMacKbdTargets.forEach(target => (target.hidden = false))
    }
  }
}
